import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { db, storage } from '../Firebase';
import { collection, addDoc, onSnapshot, deleteDoc, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  Upload, 
  X, 
  Check, 
  UserPlus,
  ArrowUp,
  ArrowDown,
  User,
  Briefcase
} from 'lucide-react';
import Swal from 'sweetalert2';
import { motion, AnimatePresence } from 'framer-motion';
import { fileToBase64 } from '../utils/fileHelper';

const MemberManager = () => {
  const [members, setMembers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    description: '',
    image: null,
    imageUrl: ''
  });

  useEffect(() => {
    const q = query(collection(db, 'members'), orderBy('order', 'asc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setMembers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 800 * 1024) {
        Swal.fire('Error', 'Image size should not exceed 800KB. Please upload an image below 800KB.', 'error');
        return;
      }
      setFormData({ ...formData, image: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.image && !formData.imageUrl) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Member photo is compulsory. Please upload a member photo.',
        confirmButtonColor: '#0D47A1'
      });
      return;
    }

    setLoading(true);
    try {
      let finalImageUrl = formData.imageUrl;

      if (formData.image) {
        finalImageUrl = await fileToBase64(formData.image);
      }

      const data = {
        name: formData.name,
        designation: formData.designation,
        description: formData.description,
        imageUrl: finalImageUrl,
        updatedAt: new Date()
      };

      if (editingId) {
        await updateDoc(doc(db, 'members', editingId), data);
        Swal.fire('Success', 'Member updated successfully', 'success');
      } else {
        const nextOrder = members.length > 0 ? Math.max(...members.map(m => m.order || 0)) + 1 : 0;
        await addDoc(collection(db, 'members'), { ...data, order: nextOrder, createdAt: new Date() });
        Swal.fire('Success', 'Member added successfully', 'success');
      }

      resetForm();
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Something went wrong', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', designation: '', description: '', image: null, imageUrl: '' });
    setEditingId(null);
    setIsModalOpen(false);
  };

  const handleDelete = async (member) => {
    const result = await Swal.fire({
      title: 'Delete Member?',
      text: `Are you sure you want to remove ${member.name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, remove them'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'members', member.id));
        if (member.imageUrl.includes('firebasestorage')) {
          await deleteObject(ref(storage, member.imageUrl));
        }
        Swal.fire('Removed!', 'Member has been removed.', 'success');
      } catch (error) {
        Swal.fire('Error', 'Failed to delete member', 'error');
      }
    }
  };

  const moveMember = async (index, direction) => {
    const newMembers = [...members];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= members.length) return;

    const temp = newMembers[index].order;
    newMembers[index].order = newMembers[targetIndex].order;
    newMembers[targetIndex].order = temp;

    try {
      await updateDoc(doc(db, 'members', newMembers[index].id), { order: newMembers[index].order });
      await updateDoc(doc(db, 'members', newMembers[targetIndex].id), { order: newMembers[targetIndex].order });
    } catch (error) {
      console.error(error);
    }
  };

  const startEdit = (member) => {
    setFormData({
      name: member.name,
      designation: member.designation,
      description: member.description,
      imageUrl: member.imageUrl,
      image: null
    });
    setEditingId(member.id);
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary-dark">Member Management</h1>
          <p className="text-gray-500 font-medium">Manage your bank's leadership and staff profiles.</p>
        </div>
        <button
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          className="flex items-center gap-2 bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-2xl shadow-lg shadow-primary/20 transition-all active:scale-95 font-bold"
        >
          <UserPlus className="w-5 h-5" />
          Add New Member
        </button>
      </div>

      {/* Members List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <AnimatePresence>
          {members.map((member, index) => (
            <motion.div
              key={member.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group hover:shadow-xl transition-all"
            >
              <div className="relative h-64 overflow-hidden bg-gray-50">
                <img src={member.imageUrl} alt={member.name} className="w-full h-full object-contain" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                
                {/* Actions */}
                <div className="absolute top-4 right-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => startEdit(member)} className="p-2 bg-white text-primary rounded-xl hover:bg-primary hover:text-white transition-all shadow-lg"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(member)} className="p-2 bg-white text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-all shadow-lg"><Trash2 className="w-4 h-4" /></button>
                </div>

                {/* Reorder Controls */}
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  <button 
                    disabled={index === 0}
                    onClick={() => moveMember(index, -1)}
                    className="p-2 bg-white/20 backdrop-blur-md text-white rounded-xl hover:bg-white/40 disabled:opacity-30 transition-all"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button 
                    disabled={index === members.length - 1}
                    onClick={() => moveMember(index, 1)}
                    className="p-2 bg-white/20 backdrop-blur-md text-white rounded-xl hover:bg-white/40 disabled:opacity-30 transition-all"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>

                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <h3 className="text-xl font-bold mb-1">{member.name}</h3>
                  <p className="text-amber-400 font-extrabold text-[11px] uppercase tracking-[0.15em]">{member.designation}</p>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {member.description && (
                  <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">{member.description}</p>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Modal */}
      {isModalOpen && createPortal(
        <div className="fixed inset-0 bg-primary-dark/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            <div className="p-8 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold text-primary-dark">{editingId ? 'Edit Member' : 'Add New Member'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Full Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    placeholder="e.g. Rajendra Patil"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Designation</label>
                  <input
                    type="text"
                    required
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    placeholder="e.g. Chairman"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700 ml-1">Member Photo (Max 800KB)</label>
                <div className="relative h-48 group">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div className="h-full border-2 border-dashed border-gray-200 rounded-3xl flex flex-col items-center justify-center gap-3 bg-gray-50 group-hover:bg-gray-100 group-hover:border-primary/30 transition-all overflow-hidden">
                    {formData.image || formData.imageUrl ? (
                      <div className="relative w-full h-full">
                        <img 
                          src={formData.image ? URL.createObjectURL(formData.image) : formData.imageUrl} 
                          className="w-full h-full object-contain" 
                          alt="Preview"
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Upload className="w-8 h-8 text-white" />
                          <p className="text-white font-bold ml-2">Change Image</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center">
                        <User className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                        <p className="text-gray-500 font-medium">Upload Member Photo</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-8 py-4 bg-gray-100 text-gray-600 font-bold rounded-2xl hover:bg-gray-200 transition-all active:scale-95"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-[2] px-8 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-primary-dark shadow-lg shadow-primary/20 transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      {editingId ? 'Update Member' : 'Add Member'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MemberManager;
